import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { BsArrowLeft } from "react-icons/bs";
import ClipLoader from "react-spinners/ClipLoader";
import { API } from "../../../../constants/api";
import useAuthAxios from "../../../../utils/baseAxios";
import useIdempotencyKey from "../../../../hooks/useIdempotencyKey";
import { errorMessage } from "../../../../utils/errorMessage";
import OtpPin from "../../../../components/OtpPin";
import { formatAmount } from "../../../../utils/formatTime";
import GetRates from "../../../../components/utils/GetRates";
import { useUser } from "../../../../context/user-context";
import { AirtimePaymentSource } from "./types";
import { invalidateActivityQueries } from "../../../../utils/invalidateActivityQueries";

type AirtimePreviewProps = {
  setOpenAirtimePreview: (open: boolean) => void;
  setOpenAirtime: (open: boolean) => void;
  amount: string;
  phone: string;
  network: string;
  networkName: string;
  paymentSource: AirtimePaymentSource;
  setOpenAirtimeSuccess: (open: boolean) => void;
};

const AirtimePreview: React.FC<AirtimePreviewProps> = ({
  setOpenAirtimePreview,
  setOpenAirtime,
  amount,
  phone,
  network,
  networkName,
  paymentSource,
  setOpenAirtimeSuccess,
}) => {
  const axiosInstance = useAuthAxios();
  const idempotency = useIdempotencyKey();
  const queryClient = useQueryClient();
  const { refetch } = useUser();
  const [addPin, setAddPin] = useState(false);
  const [otp, setOtp] = useState("");

  const usdtRate = GetRates("USDT");

  const buyAirtime = async (data: {
    amount: number;
    phone: string;
    network: string;
    pin: string;
    paymentSource: AirtimePaymentSource;
  }) => {
    const response = await axiosInstance.post(API.buyAirtime, data, idempotency.config());
    return response.data;
  };

  const completeBuy = useMutation({
    mutationFn: buyAirtime,
    onSuccess: (response) => {
      idempotency.rotate();
      toast.success(response?.message ?? "Airtime purchase successful");
      invalidateActivityQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: ["userDetails"] });
      queryClient.invalidateQueries({
        queryKey: ["naira-wallet-transactions"],
      });
      refetch();
      setTimeout(() => {
        setOpenAirtimePreview(false);
        setOpenAirtimeSuccess(true);
      }, 2000);
    },
    onError: (error: { data?: { message?: string }; message?: string }) => {
      idempotency.rotateIfAnswered(error);
      toast.error(
        errorMessage(error?.data?.message || error?.message || "Purchase failed")
      );
    },
  });

  return (
    <div className="fixed inset-0 flex items-start justify-start overflow-auto bg-[#E7EBEE] pb-16 pt-16 font-manrope dark:bg-primary_dark dark:bg-opacity-95 backdrop-blur-sm">
      <div className="mx-auto w-11/12 rounded-xl border bg-white p-6 dark:border-[#303030] dark:bg-[#192025] mds:w-8/12 lgss:w-2/5 xxl:w-1/3">
        <div className="flex w-full items-center justify-start gap-6">
          <button
            type="button"
            onClick={() => {
              setOpenAirtime(true);
              setOpenAirtimePreview(false);
            }}
          >
            <BsArrowLeft className="text-[20px] text-black dark:text-white" />
          </button>
          <h4 className="font-sora text-[20px] font-semibold text-gray-900 dark:text-gray-100">
            Preview Airtime
          </h4>
        </div>

        {addPin ? (
          <div className="mt-12">
            <h4 className="mb-4 text-center font-sora text-[20px] font-semibold text-gray-900 dark:text-gray-100">
              Enter transaction PIN
            </h4>
            <OtpPin otp={otp} input={4} setOtp={setOtp} />
            <button
              type="button"
              disabled={completeBuy.isPending || otp.length < 4}
              onClick={() => {
                completeBuy.mutate({
                  amount: Number(amount),
                  phone,
                  network,
                  pin: otp,
                  paymentSource,
                });
              }}
              className="mt-12 flex h-[48px] w-full items-center justify-center rounded-xl bg-secondary_button text-[15px] font-medium text-white disabled:bg-gray-400"
            >
              {completeBuy.isPending ? (
                <ClipLoader color="#FFFFFF" size={30} />
              ) : (
                "Buy Airtime"
              )}
            </button>
          </div>
        ) : (
          <div className="mt-10 w-full">
            <div className="border-b border-gray-200 py-5 dark:border-gray-600">
              <h4 className="text-[13px] font-bold text-secondary_button">
                Amount
              </h4>
              <h4 className="mt-2 text-[16px] text-[#13181B] dark:text-gray-100">
                {formatAmount(amount)} NGN
              </h4>
              {paymentSource === "USDT" && (
                <h4 className="mt-2 text-[12px] font-bold text-[#BE6B0A]">
                  -{" "}
                  {amount && usdtRate
                    ? formatAmount(Number(amount) / Number(usdtRate))
                    : "0.00"}{" "}
                  USDT
                </h4>
              )}
            </div>
            <div className="border-b border-gray-200 py-5 dark:border-gray-600">
              <h4 className="text-[13px] font-bold text-secondary_button">
                Wallet
              </h4>
              <h4 className="mt-2 text-[16px] text-[#13181B] dark:text-gray-100">
                {paymentSource === "NGN"
                  ? "Naira Wallet (NGN)"
                  : "Tether (USDT)"}
              </h4>
            </div>
            <div className="border-b border-gray-200 py-5 dark:border-gray-600">
              <h4 className="text-[13px] font-bold text-secondary_button">
                Network
              </h4>
              <h4 className="mt-2 text-[16px] uppercase text-[#13181B] dark:text-gray-100">
                {networkName || network}
              </h4>
            </div>
            <div className="border-b border-gray-200 py-5 dark:border-gray-600">
              <h4 className="text-[13px] font-bold text-secondary_button">
                Recipient phone no
              </h4>
              <h4 className="mt-2 text-[16px] text-[#13181B] dark:text-gray-100">
                {phone}
              </h4>
            </div>
            <button
              type="button"
              onClick={() => setAddPin(true)}
              className="mt-12 flex h-[52px] w-full items-center justify-center rounded-xl bg-secondary_button text-[15px] font-medium text-white"
            >
              Proceed
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AirtimePreview;
